import React, { useContext, useState, useEffect } from "react";
import "../styles/Basket.css";
import { ApplicationContext } from "./ApplicationContext";
import IndentEntryService from "../services/IndentEntryService";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatNumber, formatNumberKG, formatNumberWithoutPostfix, priceWithPDV, discountedPrice } from './utils';
import log from "../services/logger";
import {
  addQuantity,
  removeQuantity
} from './utils'; // Adjust the path if necessary

function isValidQuantity(value, s, m) {
  value = Number(value);
  if (isNaN(value) || value < s) {
    return false;
  }

  const tolerance = 0.001;

  // Check if value is equal to starting quantity
  if (Math.abs(value - s) < tolerance) {
    return true;
  }

  // Check if value is divisible by minimum quantity
  if (value % m < tolerance || Math.abs(value % m - m) < tolerance) {
    return true;
  }

  return false;
}

function getValidQuantity(value, s, m) {
  value = Number(value);

  if (isNaN(value) || value < s) {
    return s;
  }

  if (Math.abs(value - s) < 0.001) {
    return s;
  }

  if (value >= s && (value % m < 0.001 || Math.abs(value % m - m) < 0.001)) {
    return value;
  }

  // Adjust upwards to the next multiple of m
  const n = Math.ceil(value / m);
  const newValue = n * m;

  if (newValue < s) {
    return s;
  }

  return Number(newValue.toFixed(2));
}

const Basket = () => {
  const { setBasketItems, basketItems, removeAllBasketItems, loggedInClient, removeBasketItem } = useContext(ApplicationContext);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [comment, setComment] = useState("");
  const [missingItemQuantities, setMissingItemQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [paymentType, setPaymentType] = useState("");
  const [isDeliveryAddressEditable, setIsDeliveryAddressEditable] = useState(true);

  useEffect(() => {
    if (loggedInClient && loggedInClient.deliveryAddressList) {
      if (loggedInClient.deliveryAddressList.length === 1) {
        setDeliveryAddress(loggedInClient.deliveryAddressList[0]);
        setIsDeliveryAddressEditable(false);
      } else {
        setIsDeliveryAddressEditable(true);
      }
    }
    const newMissingItemQuantities = validateItemQuantities(basketItems);
    setMissingItemQuantities(newMissingItemQuantities);
  }, [basketItems, loggedInClient]);

  const validateItemQuantities = (basketItems) => {
    let missingQuantities = {};
    let noccoItemsQuantity = 0;

    for (let item of basketItems) {
      if (item.article.brand.brandName === "NOCCO") {
        noccoItemsQuantity += item.quantity;
      }

      if (
        !isValidQuantity(
          item.quantity,
          item.article.startingQuantityDemand,
          item.article.minimumQuantityDemand
        )
      ) {
        const adjustedQuantity = getValidQuantity(
          item.quantity,
          item.article.startingQuantityDemand,
          item.article.minimumQuantityDemand
        );
        const missingQuantity = adjustedQuantity - item.quantity;

        missingQuantities[item.article.name] = {
          article: item.article,
          missingQuantity: missingQuantity,
        };
      }
    }

    // NOCCO specific validation
    if (noccoItemsQuantity % 24 !== 0 && noccoItemsQuantity > 0) {
      const noccoArticle = basketItems.find(
        (item) => item.article.brand.brandName === "NOCCO"
      ).article;
      missingQuantities["NOCCO"] = {
        article: noccoArticle,
        missingQuantity: 24 - (noccoItemsQuantity % 24),
      };
    }

    return missingQuantities;
  };

  const handleQuantityChange = (itemIndex, value) => {
    const article = basketItems[itemIndex].article;
    const startingQuantityDemand = article.startingQuantityDemand;
    const minimumQuantityDemand = article.minimumQuantityDemand;

    let adjustedQuantity;
    if (value === "") {
      adjustedQuantity = "";
    } else {
      adjustedQuantity = getValidQuantity(
        value,
        startingQuantityDemand,
        minimumQuantityDemand
      );
    }

    const updatedBasketItems = basketItems.map((item, i) => {
      if (i === itemIndex) {
        return { ...item, quantity: adjustedQuantity };
      }
      return item;
    });
    setBasketItems(updatedBasketItems);
  };

  const confirmOrder = async () => {
    if (!paymentType) {
      toast.error('Molimo izaberite tip plaćanja!');
      return;
    }

    if (!basketItems?.length) {
      toast.error('Korpa je prazna!');
      return;
    }

    setIsLoading(true);
    try {
      const itemsToCreate = basketItems
        .filter(item => item.quantity > 0)
        .map(item => ({
          customerId: loggedInClient.customer_id,
          articleName: item.article.name,
          requestedQuantity: item.quantity,
          deliveryAddress,
        }));

      await IndentEntryService.createIndentEntries(itemsToCreate, paymentType, comment);
      removeAllBasketItems();
      setComment("");
      setPaymentType("");
      toast.success('Uspešno kreirana porudžbina!');
    } catch (error) {
      log.error(error);
      toast.error('Neuspešno ažuriranje! ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const brandDiscount = (brand) => {
    const brandName = brand.brandName;
    const discountForTheBrand = loggedInClient?.discounts?.find((item) => item.brand.brandName === brandName);
    return discountForTheBrand ? discountForTheBrand.discount : 0;
  };

  const articlePriceWithDiscount = (article) => {
    return (Number(article.retailPrice) * (1 - Number(brandDiscount(article.brand)) / 100)).toFixed(2);
  };

  const handleDeliveryAddressChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue) {
      const parsedDeliveryAddress = JSON.parse(selectedValue);
      setDeliveryAddress(parsedDeliveryAddress);
    } else {
      setDeliveryAddress("");
    }
  };

  const totalCost = basketItems
    ? basketItems.reduce((acc, item) => acc + articlePriceWithDiscount(item.article) * item.quantity, 0)
    : 0;

  const totalWeight = basketItems
    ? basketItems.reduce((acc, item) => {
      const brutoMass = parseFloat(item.article.brutoMass);
      const requestedQuantity = parseFloat(item.quantity);
      if (isNaN(brutoMass) || isNaN(requestedQuantity)) {
        return acc;
      }
      return acc + brutoMass * requestedQuantity;
    }, 0)
    : 0;

  const totalNumberOfPackages = basketItems
    ? basketItems.reduce((acc, item) => {
      const quantityPerTransportPackage = item.article.quantityPerTransportPackage;
      const requestedQuantity = parseFloat(item.quantity);
      if (isNaN(quantityPerTransportPackage) || isNaN(requestedQuantity)) {
        return acc;
      }
      return acc + requestedQuantity / quantityPerTransportPackage;
    }, 0)
    : 0;

  const getPDV = (basketItems) => {
    if (basketItems && basketItems.length > 0) {
      return basketItems[0].article.pdv;
    }
    return 0;
  };

  const pdv = getPDV(basketItems);

  const allItemsHaveValidQuantities = basketItems.every(
    (item) =>
      item.quantity > 0 &&
      isValidQuantity(
        item.quantity,
        item.article.startingQuantityDemand,
        item.article.minimumQuantityDemand
      )
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="flex items-center mb-8">
        <i className="fas fa-shopping-cart text-2xl text-blue-500 mr-3"></i>
        <h3 className="text-2xl font-bold text-gray-800">Korpa</h3>
      </div>

      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Cart Items */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Redni Broj
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Artikal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Količina
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Težina
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akcije
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {basketItems.length > 0 ? (
                basketItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.article.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          className="p-1 rounded-full hover:bg-gray-100"
                          onClick={() => {
                            const currentQty = Number(item.quantity) || 0;
                            handleQuantityChange(
                              index,
                              removeQuantity(currentQty, item.article)
                            );
                          }}
                        >
                          <i className="fas fa-minus text-gray-600"></i>
                        </button>
                        <input
                          type="number"
                          className="w-20 text-center border rounded-md p-1"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                        />
                        <button
                          className="p-1 rounded-full hover:bg-gray-100"
                          onClick={() => {
                            const currentQty = Number(item.quantity) || 0;
                            handleQuantityChange(
                              index,
                              addQuantity(currentQty, item.article)
                            );
                          }}
                        >
                          <i className="fas fa-plus text-gray-600"></i>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumberKG(item.article.brutoMass * item.quantity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => removeBasketItem(item.article)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    Korpa je prazna
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Comment */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-4">Napomena</h4>
            <textarea
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="4"
              placeholder="Unesite napomenu..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          {/* Delivery Address */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-4">Adresa dostave</h4>
            {isDeliveryAddressEditable ? (
              <select
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={JSON.stringify(deliveryAddress)}
                onChange={(e) => setDeliveryAddress(JSON.parse(e.target.value))}
              >
                <option value="">Izaberite adresu dostave</option>
                {loggedInClient?.deliveryAddressList?.map((addr, index) => (
                  <option key={index} value={JSON.stringify(addr)}>
                    {addr.city}, {addr.address}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-600">
                {deliveryAddress.city}, {deliveryAddress.address}
              </p>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Payment Type */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-4">Tip plaćanja</h4>
            <select
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              required
            >
              <option value="">Izaberite tip plaćanja</option>
              <option value="faktura">Faktura</option>
              <option value="gotovina">Gotovina</option>
              <option value="gotovinski racun">Gotovinski račun</option>
            </select>
          </div>

          {/* Total Weight */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center">
              <h4 className="text-lg font-semibold text-gray-700">Ukupna težina:</h4>
              <span className="text-xl font-bold text-gray-900">
                {formatNumberWithoutPostfix(totalWeight)} KG
              </span>
            </div>
          </div>

          {/* Confirm Order Button */}
          <button
            className={`w-full py-3 px-6 rounded-lg text-white font-semibold transition-colors duration-200 
              ${(!deliveryAddress || !paymentType || !basketItems.length)
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600'
              }`}
            onClick={confirmOrder}
            disabled={!deliveryAddress || !paymentType || !basketItems.length}
          >
            Potvrdi porudžbinu
          </button>
        </div>
      </div>
    </div>
  );
};

export default Basket;
